/**
 * Progress reporting for an upload whose only visible surface is a node
 * INSIDE the editor — the card / comment image placeholder and file chip.
 *
 * Those NodeViews have no prop channel back to the composer: the editor
 * extensions are built once per surface, so nothing can hand a React
 * value down to them. The same constraint is why Retry / Dismiss travel
 * as window events. Progress can't ride an event stream (it ticks many
 * times a second), and it can't ride a node ATTR either — a new
 * default-valued attr is back-filled onto every existing node at
 * serialization time, which makes unrelated bodies read as changed.
 *
 * So it rides the upload registry, the one store built to be readable
 * from anywhere without React context, keyed on the node's placeholder
 * id. The NodeView already knows that id — it is on the node — and reads
 * it with `useUploadProgress(placeholderId)`.
 *
 * Registering the placeholder as its own key also covers the PRESIGN leg
 * in the unload guard, which the attachment-id key cannot: that key does
 * not exist until presign has returned.
 */

import type { PutProgress } from './put-with-progress'
import {
  markUploadFinished,
  markUploadStarted,
  throttleProgress,
  updateUploadProgress,
} from './upload-registry'

export interface PlaceholderProgressChannel {
  /** Pass straight to the upload hook's `onProgress`; already throttled. */
  onProgress: (progress: PutProgress) => void
  /**
   * MUST run on every exit — success, failure and abort alike. A missed
   * call leaks an entry into a module-scope registry nothing collects,
   * and the tab then warns about a phantom upload on every reload for
   * the rest of its life.
   */
  finish: () => void
}

export function trackPlaceholderProgress(placeholderId: string): PlaceholderProgressChannel {
  markUploadStarted(placeholderId)
  return {
    // The registry ignores a key that is no longer in flight, so a tick
    // that lands after `finish()` cannot resurrect the entry.
    onProgress: throttleProgress((progress) => updateUploadProgress(placeholderId, progress)),
    finish: () => markUploadFinished(placeholderId),
  }
}
